import { FC, createContext, useContext, useState, useEffect, useCallback, useMemo, PropsWithChildren } from 'react';
import { LocationOptionsMap, getDefaultLocation, getLocation } from '../constants/locations';
import { storageKeys } from '../constants/constants';

export type LocationValue = string;

export type LocationContextType = {
	locationCityId: LocationValue;
	locationName: string;
	locationPath: string;
	updateLocation?(newLocationCityId: LocationValue): void;
};

const defaultLocation = getDefaultLocation();

const defaultContextValue: LocationContextType = {
	locationCityId: defaultLocation.cityId,
	locationName: defaultLocation.name,
	locationPath: defaultLocation.path,
};

export const LocationContext = createContext<LocationContextType>(defaultContextValue);

export const LocationProvider: FC<PropsWithChildren<{ value: LocationValue }>> = ({ value, children }) => {
	const [locationCityId, setLocationCityId] = useState<LocationValue>(() => {
		// If we have a value, we can use it
		if (value) return getLocation({ id: value }).cityId;

		return getDefaultLocation().cityId;
	});

	useEffect(() => {
		if (value) {
			return;
		}

		const storagedLocationId = localStorage.getItem(storageKeys.locationId);
		const foundLocation = getLocation({ id: storagedLocationId });

		setLocationCityId(foundLocation.cityId);
	}, []);

	const updateLocation = useCallback((newLocationCityId: LocationValue) => {
		setLocationCityId(newLocationCityId);
		localStorage.setItem(storageKeys.locationId, newLocationCityId);
	}, []);

	const contextValue = useMemo(
		() => ({
			locationCityId,
			locationName: LocationOptionsMap[locationCityId]?.name ?? '',
			locationPath: LocationOptionsMap[locationCityId]?.path ?? '',
			updateLocation,
		}),
		[locationCityId, updateLocation]
	);

	// on value change, update location
	useEffect(() => {
		if (value) {
			const foundLocation = getLocation({ id: value });

			updateLocation(foundLocation.cityId);
		} else {
			if (typeof window === 'undefined') {
				return;
			}

			const storagedLocationId = localStorage.getItem(storageKeys.locationId);

			const foundLocation = getLocation({ id: storagedLocationId });

			setLocationCityId(foundLocation.cityId);
		}
	}, [updateLocation, value]);

	return <LocationContext.Provider value={contextValue}>{children}</LocationContext.Provider>;
};

export const useLocation = () => {
	const context = useContext(LocationContext);

	if (context === undefined) {
		throw new Error('useLocation must be used within a LocationProvider');
	}

	return context;
};
